import { useCallback, useMemo, useState } from "react";
import { msgid, ngettext, t } from "ttag";

import {
  useGetDashboardQuery,
  useListCollectionItemsQuery,
  useListPermissionsGroupsQuery,
} from "metabase/api";
import { useDispatch, useSelector } from "metabase/redux";
import {
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Icon,
  Loader,
  Menu,
  Pill,
  Stack,
  Text,
  TextInput,
  Title,
} from "metabase/ui";
import type { CollectionId } from "metabase-types/api";

import { updateDashboardPermission } from "../../permissions";

// ---------------------------------------------------------------------------
// Types (mirrored from the permissions redux state)

type DashCardEntry = { value: string };
type DashTabEntry = { value: string; cards?: Record<string, DashCardEntry> };
type DashEntry = { value: string; tabs?: Record<string, DashTabEntry> };
type GroupPerms = Record<string, DashEntry>;
type PermissionsGraph = Record<string, GroupPerms>;

type PermGroup = { id: number | string; name: string };

type ChangeFn = (args: {
  groupId: string;
  dashboardId: string;
  tabId?: string;
  cardId?: string;
  value: string;
}) => void;

// ---------------------------------------------------------------------------
// Helpers

function getEffectivePerm(
  permissions: PermissionsGraph,
  groupId: string,
  dashboardId: string,
  tabId?: string,
  cardId?: string,
): string {
  const dash = permissions[groupId]?.[dashboardId];
  if (!dash) {
    return "view";
  }
  if (cardId && tabId) {
    return dash.tabs?.[tabId]?.cards?.[cardId]?.value ?? "view";
  }
  if (tabId) {
    return dash.tabs?.[tabId]?.value ?? "view";
  }
  return dash.value ?? "view";
}

/**
 * The groups denied a given dashboard / tab / card.
 *
 * The permission is binary and only denials are ever stored, so the state of any
 * one row is exactly "which groups are blocked" - a set. That is what this UI
 * shows, instead of a dropdown per group.
 */
function deniedGroups(
  permissions: PermissionsGraph,
  groups: PermGroup[],
  dashboardId: string,
  tabId?: string,
  cardId?: string,
): PermGroup[] {
  return groups.filter(
    (g) =>
      getEffectivePerm(
        permissions,
        String(g.id),
        dashboardId,
        tabId,
        cardId,
      ) === "no-access",
  );
}

// ---------------------------------------------------------------------------
// The one control this UI is built from: a row of "blocked group" pills plus a
// menu to add another. Its width is driven by how many groups are *restricted*,
// not by how many groups exist - so it does not grow as groups are added.

function RestrictedFor({
  groups,
  denied,
  onChange,
  size = "sm",
  emptyLabel,
}: {
  groups: PermGroup[];
  denied: PermGroup[];
  onChange: (groupId: string, value: string) => void;
  size?: "sm" | "xs";
  emptyLabel?: string;
}) {
  const deniedIds = useMemo(
    () => new Set(denied.map((g) => String(g.id))),
    [denied],
  );
  const available = useMemo(
    () => groups.filter((g) => !deniedIds.has(String(g.id))),
    [groups, deniedIds],
  );

  return (
    <Group gap="xs" wrap="wrap" style={{ flex: 1 }}>
      {denied.length === 0 ? (
        <Text c="text-disabled" size={size}>
          {emptyLabel ?? t`Everyone can view`}
        </Text>
      ) : (
        denied.map((g) => (
          <Pill
            key={g.id}
            withRemoveButton
            size={size}
            onRemove={() => onChange(String(g.id), "view")}
            style={{
              backgroundColor: "var(--mb-color-background-error)",
              color: "var(--mb-color-error)",
              fontWeight: 600,
            }}
          >
            {g.name}
          </Pill>
        ))
      )}

      {available.length > 0 && (
        <Menu position="bottom-start" withinPortal>
          <Menu.Target>
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<Icon name="add" size={10} />}
            >
              {t`Restrict a group`}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {available.map((g) => (
              <Menu.Item
                key={g.id}
                onClick={() => onChange(String(g.id), "no-access")}
              >
                {g.name}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Tab / question drill-down

function TabView({
  dashboardId,
  groups,
  permissions,
  onChangePermission,
}: {
  dashboardId: number;
  groups: PermGroup[];
  permissions: PermissionsGraph;
  onChangePermission: ChangeFn;
}) {
  const dashId = String(dashboardId);
  const { data: fullDashboard, isLoading } = useGetDashboardQuery({
    id: dashboardId,
  });

  const tabs: any[] = fullDashboard?.tabs ?? [];
  const dashcards: any[] = useMemo(
    () => fullDashboard?.dashcards ?? [],
    [fullDashboard],
  );

  const cardsByTab = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const dc of dashcards) {
      if (!dc.card_id) {
        continue;
      }
      const key =
        dc.dashboard_tab_id != null
          ? String(dc.dashboard_tab_id)
          : "__no_tab__";
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(dc);
    }
    return map;
  }, [dashcards]);

  if (isLoading) {
    return (
      <Flex justify="center" py="sm">
        <Loader size="sm" />
      </Flex>
    );
  }

  if (tabs.length === 0 && dashcards.filter((dc) => dc.card_id).length === 0) {
    return (
      <Text c="text-secondary" size="sm" py="xs" pl="xl">
        {t`This dashboard has no tabs or questions.`}
      </Text>
    );
  }

  const cardRow = (dc: any, tabId?: string) => {
    const cardId = String(dc.card_id);
    return (
      <Flex key={dc.id} align="flex-start" gap="md" py={4} pl="xl">
        <Text size="xs" c="text-secondary" style={{ flex: "0 0 190px" }}>
          {dc.card?.name ?? t`Question`}
        </Text>
        <RestrictedFor
          size="xs"
          groups={groups}
          denied={deniedGroups(permissions, groups, dashId, tabId, cardId)}
          onChange={(groupId, value) =>
            onChangePermission({
              groupId,
              dashboardId: dashId,
              tabId,
              cardId,
              value,
            })
          }
        />
      </Flex>
    );
  };

  return (
    <Stack gap={2} pl="xl" py="xs">
      {tabs.map((tab: any) => {
        const tabId = String(tab.id);
        return (
          <Box key={tabId}>
            <Flex align="flex-start" gap="md" py={4}>
              <Flex
                align="center"
                gap="xs"
                style={{ flex: "0 0 200px" }}
                c="text-secondary"
              >
                <Icon name="chevronright" size={10} />
                <Text fw={600} size="sm">
                  {tab.name ?? t`Tab`}
                </Text>
              </Flex>
              <RestrictedFor
                groups={groups}
                denied={deniedGroups(permissions, groups, dashId, tabId)}
                onChange={(groupId, value) =>
                  onChangePermission({
                    groupId,
                    dashboardId: dashId,
                    tabId,
                    value,
                  })
                }
              />
            </Flex>
            {(cardsByTab[tabId] ?? []).map((dc) => cardRow(dc, tabId))}
          </Box>
        );
      })}

      {(cardsByTab["__no_tab__"] ?? []).map((dc) => cardRow(dc, undefined))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main section

export function DashboardPermissionsSection({
  collectionId,
}: {
  collectionId: CollectionId;
}) {
  const dispatch = useDispatch();

  const permissions: PermissionsGraph = useSelector(
    (s: any) => s.admin.permissions.dashboardPermissions ?? {},
  );
  // 0.63 removed the entities framework (state.entities.groups); groups now come
  // from RTK Query.
  const { data: allGroups = [] } = useListPermissionsGroupsQuery({});

  // Administrators always have access, so they are not offered as a choice at
  // all - a disabled "Can view" cell for them was pure noise. Superusers bypass
  // dashboard permissions in the backend too.
  const groups: PermGroup[] = useMemo(
    () => allGroups.filter((g: any) => g.name !== "Administrators"),
    [allGroups],
  );

  const { data: collectionItems, isLoading } = useListCollectionItemsQuery({
    id: collectionId,
    models: ["dashboard"],
  });

  const dashboards = useMemo(
    () => collectionItems?.data ?? [],
    [collectionItems],
  );

  const [expandedDashId, setExpandedDashId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const visibleDashboards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return dashboards;
    }
    return dashboards.filter((d: any) =>
      (d.name ?? "").toLowerCase().includes(q),
    );
  }, [dashboards, search]);

  const handlePermissionChange = useCallback<ChangeFn>(
    ({ groupId, dashboardId, tabId, cardId, value }) => {
      dispatch(
        updateDashboardPermission({
          groupId,
          dashboardId,
          tabId,
          cardId,
          value,
        }),
      );
    },
    [dispatch],
  );

  if (isLoading) {
    return (
      <Box p="md">
        <Flex justify="center" py="lg">
          <Loader />
        </Flex>
      </Box>
    );
  }

  if (dashboards.length === 0) {
    return null;
  }

  return (
    <Box p="md" pt={0}>
      <Divider my="md" />

      <Flex align="center" justify="space-between" mb={4} gap="md">
        <Title order={5}>{t`Dashboard permissions`}</Title>
        {dashboards.length > 5 && (
          <TextInput
            size="xs"
            w={220}
            placeholder={t`Search dashboards`}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<Icon name="search" size={12} />}
          />
        )}
      </Flex>

      <Text c="text-secondary" size="sm" mb="md">
        {t`Everyone with access to this collection can see these dashboards. Restrict a group to hide a dashboard, tab or question from it.`}
      </Text>

      {visibleDashboards.length === 0 ? (
        <Text c="text-disabled" size="sm" py="md">
          {t`No dashboards match your search.`}
        </Text>
      ) : (
        <Stack gap={0}>
          {visibleDashboards.map((dash: any) => {
            const dashId = String(dash.id);
            const isExpanded = expandedDashId === dash.id;
            const denied = deniedGroups(permissions, groups, dashId);

            // How much is restricted below the dashboard level, so the row can
            // say so without being expanded.
            const nestedCount = groups.reduce((acc, g) => {
              const entry = permissions[String(g.id)]?.[dashId];
              const tabs = entry?.tabs ?? {};
              return (
                acc +
                Object.values(tabs).filter(
                  (tb: any) =>
                    tb?.value === "no-access" ||
                    Object.values(tb?.cards ?? {}).some(
                      (c: any) => c?.value === "no-access",
                    ),
                ).length
              );
            }, 0);

            return (
              <Box
                key={dash.id}
                style={{
                  borderTop: "1px solid var(--mb-color-border)",
                }}
              >
                <Flex align="flex-start" gap="md" py="sm">
                  <Box style={{ flex: "0 0 200px" }}>
                    <Text fw={600} size="sm">
                      {dash.name}
                    </Text>
                    {nestedCount > 0 && (
                      <Text c="text-disabled" size="xs">
                        {ngettext(
                          msgid`${nestedCount} tab or question restricted`,
                          `${nestedCount} tabs or questions restricted`,
                          nestedCount,
                        )}
                      </Text>
                    )}
                  </Box>

                  <RestrictedFor
                    groups={groups}
                    denied={denied}
                    onChange={(groupId, value) =>
                      handlePermissionChange({
                        groupId,
                        dashboardId: dashId,
                        value,
                      })
                    }
                  />

                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() =>
                      setExpandedDashId(isExpanded ? null : dash.id)
                    }
                    rightSection={
                      <Icon
                        name={isExpanded ? "chevronup" : "chevrondown"}
                        size={10}
                      />
                    }
                  >
                    {isExpanded ? t`Hide details` : t`Tabs & questions`}
                  </Button>
                </Flex>

                {isExpanded && (
                  <Box
                    mb="sm"
                    style={{
                      background: "var(--mb-color-bg-light)",
                      borderRadius: 8,
                    }}
                  >
                    <TabView
                      dashboardId={dash.id}
                      groups={groups}
                      permissions={permissions}
                      onChangePermission={handlePermissionChange}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
